import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import { theme } from '@Guidelines/Theme/theme';
import formatLongDate from '@UI/Helpers/formatLongDate';
import DocumentStatusTag from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Shared/DocumentStatusTag';
import DownloadDocumentButton from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Shared/DownloadDocumentButton';
import formatRemainingToVerify from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Shared/Helpers/formatRemainingToVerify';
import useAccountDocumentTypes from '@UI/Hooks/useAccountDocumentTypes';
import type DocumentView from '@UI/Store/Documents/Views/DocumentView';
import { css } from '@emotion/react';
import { FileText } from 'lucide-react';
import type * as React from 'react';
import { Link } from 'react-router-dom';
import { Skeleton } from '@verclam/ui-kit';

const rowStyle = css`
	display: flex;
	gap: 0.875rem;
	align-items: center;
	flex-wrap: wrap;
	padding: 0.875rem 0;

	& + & {
		border-top: 1px solid ${theme.colors.border};
	}
`;

const tileStyle = css`
	display: flex;
	align-items: center;
	justify-content: center;
	flex: none;
	width: 2.375rem;
	height: 2.375rem;
	border-radius: ${theme.radii.small};
	background-color: ${theme.colors.surfaceMuted};
	color: ${theme.colors.inkSecondary};
`;

const textStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.1875rem;
	flex: 1;
	min-width: 10rem;
`;

const titleRowStyle = css`
	display: flex;
	gap: 0.5rem;
	align-items: center;
	flex-wrap: wrap;
`;

const titleLinkStyle = css`
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.large};
	font-weight: ${theme.fontWeights.semibold};
	color: ${theme.colors.ink};
	text-decoration: none;

	&:hover,
	&:focus-visible {
		text-decoration: underline;
	}
`;

const metaStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	line-height: 1.55;
	color: ${theme.colors.inkMuted};
`;

type Props = {
	readonly subject: SubjectAddress;
	readonly document: DocumentView;
};

/**
 * One row of the dossier's document list: title (the link to the review), status, document type, when it
 * was generated, what is left to verify, and the download. The type label resolves against the account's
 * document types catalogue: a skeleton stands in its place until that catalogue has loaded, and an
 * archived type still resolves — the produced document keeps its type forever.
 */
const DocumentCard: React.FC<Props> = ({ subject, document }) => {
	const { documentTypes, error: documentTypesError } = useAccountDocumentTypes();

	return (
		<li css={rowStyle}>
			<span css={tileStyle} aria-hidden="true">
				<FileText size={18} />
			</span>
			<div css={textStyle}>
				<div css={titleRowStyle}>
					<Link
						css={titleLinkStyle}
						to={`${buildSubjectPath(subject)}/documents/${document.id}?from=list`}
					>
						{document.title}
					</Link>
					<DocumentStatusTag status={document.status} />
				</div>
				{documentTypes === null && documentTypesError === null ? (
					<Skeleton width="10rem" height="1rem" animation="wave" />
				) : (
					<p css={metaStyle}>
						{documentTypes?.labelOf(document.documentTypeId) ?? 'Type inconnu'} · Généré le{' '}
						{formatLongDate(document.createdAt)} ·{' '}
						{formatRemainingToVerify(document.progress.remaining)}
					</p>
				)}
			</div>
			<DownloadDocumentButton subject={subject} document={document} />
		</li>
	);
};

export default DocumentCard;
